import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertCircle, RefreshCw, Search, Eye, ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import { Toggle } from '@/components/ui/toggle'
import { supabase } from '../supabase-client'

/**
 * Integration Activity Log Component
 *
 * Displays unified activity log for both Xero and GoCardless integrations
 * Shows webhooks, API calls, user actions, and system events
 *
 * Follows Odin Style Guide:
 * - 90% neutral colors, 10% brand green
 * - Subtle interactions and transitions
 * - Clean white backgrounds with gentle shadows
 * - Professional, uncluttered aesthetic
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user from Supabase
 */
export default function IntegrationActivityLog({ user }) {
  // State
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // Pagination
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const PAGE_SIZE = 50

  // Filters - toggleable
  const [filters, setFilters] = useState({
    // Integrations
    xero: true,
    gocardless: true,
    // Activity types
    webhook_event: true,
    api_call: true,
    user_action: true,
    system_event: true,
  })

  // Load events on mount and when filters/page change
  useEffect(() => {
    fetchEvents()
  }, [filters, page])

  /**
   * Fetch activity logs from Edge Function
   */
  const fetchEvents = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Get session for auth header
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('Authentication required')
      }

      // Build filter arrays
      const activeIntegrations = []
      if (filters.xero) activeIntegrations.push('xero')
      if (filters.gocardless) activeIntegrations.push('gocardless')

      const activeActivityTypes = []
      if (filters.webhook_event) activeActivityTypes.push('webhook_event')
      if (filters.api_call) activeActivityTypes.push('api_call')
      if (filters.user_action) activeActivityTypes.push('user_action')
      if (filters.system_event) activeActivityTypes.push('system_event')

      // Call Edge Function
      const { data, error } = await supabase.functions.invoke('integration-activity-log', {
        body: {
          action: 'list_events',
          integration: activeIntegrations,
          activity_type: activeActivityTypes,
          page,
          page_size: PAGE_SIZE
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      setEvents(data.events || [])
      setTotalCount(data.pagination?.total_count || 0)
      setTotalPages(data.pagination?.total_pages || 0)
    } catch (err) {
      console.error('[IntegrationActivityLog] Failed to fetch events:', err)
      setError(err.message || 'Failed to load activity log')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle filter toggle
   */
  const handleFilterToggle = (filterKey) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }))
    setPage(1) // Reset to first page when filter changes
  }

  /**
   * Handle search
   */
  const handleSearch = () => {
    if (!searchTerm) {
      fetchEvents()
      return
    }

    const filtered = events.filter(event => {
      const searchLower = searchTerm.toLowerCase()
      return (
        event.action?.toLowerCase().includes(searchLower) ||
        event.event_category?.toLowerCase().includes(searchLower) ||
        event.resource_id?.toLowerCase().includes(searchLower) ||
        event.endpoint?.toLowerCase().includes(searchLower) ||
        event.error_message?.toLowerCase().includes(searchLower)
      )
    })

    setEvents(filtered)
  }

  /**
   * View event details
   */
  const handleViewEvent = async (eventId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const { data, error } = await supabase.functions.invoke('integration-activity-log', {
        body: {
          action: 'get_event',
          event_id: eventId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      setSelectedEvent(data.event)
      setIsDialogOpen(true)
    } catch (err) {
      console.error('[IntegrationActivityLog] Failed to load event details:', err)
      setError(err.message)
    }
  }

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = new Date(timestamp)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  /**
   * Get status badge color
   */
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 hover:bg-green-200'
      case 'failed':
        return 'bg-red-100 text-red-800 hover:bg-red-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
    }
  }

  /**
   * Get activity type badge color
   */
  const getActivityTypeBadgeColor = (activityType) => {
    switch (activityType) {
      case 'webhook_event':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200'
      case 'api_call':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-200'
      case 'user_action':
        return 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
      case 'system_event':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
    }
  }

  /**
   * Format activity type for display
   */
  const formatActivityType = (activityType) => {
    switch (activityType) {
      case 'webhook_event':
        return 'Webhook'
      case 'api_call':
        return 'API Call'
      case 'user_action':
        return 'User Action'
      case 'system_event':
        return 'System'
      default:
        return activityType
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-2xl font-semibold text-gray-900">
          Integration Activity Log
        </CardTitle>
        <CardDescription className="text-gray-600">
          View all integration activity from Xero and GoCardless (last 30 days)
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters and Search */}
        <div className="space-y-4">
          {/* Integration Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Integrations:</span>
            <Toggle
              pressed={filters.xero}
              onPressedChange={() => handleFilterToggle('xero')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              Xero
            </Toggle>
            <Toggle
              pressed={filters.gocardless}
              onPressedChange={() => handleFilterToggle('gocardless')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              GoCardless
            </Toggle>
          </div>

          {/* Activity Type Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Activity Types:</span>
            <Toggle
              pressed={filters.webhook_event}
              onPressedChange={() => handleFilterToggle('webhook_event')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              Webhooks
            </Toggle>
            <Toggle
              pressed={filters.api_call}
              onPressedChange={() => handleFilterToggle('api_call')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              API Calls
            </Toggle>
            <Toggle
              pressed={filters.user_action}
              onPressedChange={() => handleFilterToggle('user_action')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              User Actions
            </Toggle>
            <Toggle
              pressed={filters.system_event}
              onPressedChange={() => handleFilterToggle('system_event')}
              className="data-[state=on]:bg-odin-green data-[state=on]:text-white"
            >
              System
            </Toggle>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by action, category, resource, endpoint..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button
              onClick={handleSearch}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
            >
              Search
            </Button>
            <Button
              onClick={fetchEvents}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Events Table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold text-gray-700">Time</TableHead>
                <TableHead className="font-semibold text-gray-700">Integration</TableHead>
                <TableHead className="font-semibold text-gray-700">Type</TableHead>
                <TableHead className="font-semibold text-gray-700">Category</TableHead>
                <TableHead className="font-semibold text-gray-700">Action</TableHead>
                <TableHead className="font-semibold text-gray-700">Status</TableHead>
                <TableHead className="font-semibold text-gray-700">Duration</TableHead>
                <TableHead className="font-semibold text-gray-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    Loading events...
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    No events found
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow key={event.id} className="hover:bg-gray-50">
                    <TableCell className="text-sm text-gray-600">
                      {formatTimestamp(event.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {event.integration}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getActivityTypeBadgeColor(event.activity_type)}>
                        {formatActivityType(event.activity_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 capitalize">
                      {event.event_category}
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">
                      {event.action}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(event.status)}>
                        {event.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {event.duration_ms ? `${event.duration_ms}ms` : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewEvent(event.id)}
                        className="text-odin-green hover:text-odin-green-dark hover:bg-odin-green/10"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {events.length} of {totalCount} events
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-gray-300"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-gray-300"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Event Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Event Details
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Complete information for this activity log entry
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-4">
              {/* Overview */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-700">Integration:</span>
                  <p className="text-gray-900 capitalize">{selectedEvent.integration}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Activity Type:</span>
                  <p className="text-gray-900">{formatActivityType(selectedEvent.activity_type)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Category:</span>
                  <p className="text-gray-900 capitalize">{selectedEvent.event_category}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Action:</span>
                  <p className="text-gray-900">{selectedEvent.action}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <Badge className={getStatusBadgeColor(selectedEvent.status)}>
                    {selectedEvent.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Timestamp:</span>
                  <p className="text-gray-900">{formatTimestamp(selectedEvent.created_at)}</p>
                </div>
              </div>

              {/* HTTP Details */}
              {(selectedEvent.http_method || selectedEvent.endpoint) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">HTTP Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedEvent.http_method && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Method:</span>
                        <p className="text-gray-900">{selectedEvent.http_method}</p>
                      </div>
                    )}
                    {selectedEvent.endpoint && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Endpoint:</span>
                        <p className="text-gray-900 text-sm break-all">{selectedEvent.endpoint}</p>
                      </div>
                    )}
                    {selectedEvent.duration_ms && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Duration:</span>
                        <p className="text-gray-900">{selectedEvent.duration_ms}ms</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Resource Details */}
              {(selectedEvent.resource_id || selectedEvent.resource_type) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Resource</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedEvent.resource_type && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Type:</span>
                        <p className="text-gray-900 capitalize">{selectedEvent.resource_type}</p>
                      </div>
                    )}
                    {selectedEvent.resource_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">ID:</span>
                        <p className="text-gray-900 font-mono text-sm">{selectedEvent.resource_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Request Summary */}
              {selectedEvent.request_summary && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Request Summary</h4>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.request_summary, null, 2)}
                  </pre>
                </div>
              )}

              {/* Response Summary */}
              {selectedEvent.response_summary && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Response Summary</h4>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.response_summary, null, 2)}
                  </pre>
                </div>
              )}

              {/* Error Message */}
              {selectedEvent.error_message && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-red-700 mb-2">Error Message</h4>
                  <div className="bg-red-50 p-3 rounded text-sm text-red-800">
                    {selectedEvent.error_message}
                  </div>
                </div>
              )}

              {/* Metadata */}
              {selectedEvent.metadata && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Metadata</h4>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
